import React, { useMemo, useCallback, useRef } from 'react';
import MantleDataGrid, { gcell } from './MantleDataGrid';
import { GRID } from '../styles/gridStyles';

// =============================================================================
// MantleGrid — ReactGrid-compatible ADAPTER over Glide Data Grid.
//
// This used to wrap @silevis/reactgrid, which re-renders every visible cell on
// every interaction (its internal state object is passed as a prop to each row,
// defeating its own memoization) — the source of the app-wide grid sluggishness.
// Rather than hand-porting the ~25 pages built on the old API, this adapter
// keeps the exact same contract — `rows` (header row + data rows, built with the
// `cell.*` builders from gridStyles) + `columns` + `onCellsChanged` +
// `onFocusLocationChanging` — and renders through MantleDataGrid (canvas).
//
// What pages get for free: canvas-speed interactions, range copy/paste (with
// single-value tiling), fill handle + Ctrl+D/R, Ctrl+F search, drag-resizable
// columns, and the same look as the OPEX/Structures grids.
//
// Contract mapping:
//   rows[0]                → column headers (title text taken from its cells)
//   rows[1..]              → data rows (incl. any extra "header" rows when
//                            stickyTopRows > 1 — they render as shaded rows)
//   cell.nonEditable       → read-only cell
//   onCellsChanged         → fired with [{ rowId, columnId, previousCell,
//                            newCell: { ...prev, value/text } }] per edit
//   onFocusLocationChanging({ rowId, columnId }) → fired on cell click
//                            (rowId 'header' for header clicks) — the way old
//                            pages implement click-actions and drill-downs
//   stickyLeftColumns      → freezeColumns
//   stickyBottomRows       → no bottom pinning in Glide; totals rows simply
//                            render last (same as OPEX/Structures)
//   gridProps.onSelectionChanged / onFocusLocationChanged → synthesized from
//                            Glide's selection (ReactGrid-shaped ranges)
//
// New pages should use MantleDataGrid directly (filters, fitHeight, etc.) —
// see pages/BudgetGrid.jsx and pages/People/Structures.jsx.
// =============================================================================

// Map a ReactGrid cell's CSS-variable styling onto concrete canvas colours.
const themeFromStyle = (cell) => {
  const st = cell.style || {};
  const bg = String(st.background || '');
  const color = String(st.color || '');
  const t = {};

  if (bg.includes('--grid-total-bg') || bg.includes('--grid-header-bg')) {
    t.bgCell = 'rgba(100, 116, 139, 0.22)'; t.baseFontStyle = '600 12px';
  } else if (bg.includes('--grid-year-agg-bg')) {
    t.bgCell = 'rgba(100, 116, 139, 0.18)'; t.baseFontStyle = '600 12px';
  } else if (bg.includes('--grid-quarter-agg-bg')) {
    t.bgCell = 'rgba(100, 116, 139, 0.10)'; t.baseFontStyle = '600 12px';
  } else if (bg.includes('--grid-readonly-bg')) {
    t.bgCell = 'rgba(100, 116, 139, 0.08)';
  } else if (bg.includes('--grid-manual-bg')) {
    t.bgCell = 'rgba(180, 83, 9, 0.10)';
  } else if (bg.includes('--grid-calc-bg')) {
    t.bgCell = 'rgba(29, 78, 216, 0.10)';
  } else if (bg.includes('--grid-quarter-header-bg')) {
    t.bgCell = 'rgba(100, 116, 139, 0.14)'; t.baseFontStyle = '600 12px';
  }

  if (color.includes('--grid-manual-text')) t.textDark = '#b45309';
  else if (color.includes('--grid-calc-text')) t.textDark = '#1d4ed8';
  else if (color.includes('--grid-negative') || color.includes('--grid-total-negative')) t.textDark = '#dc2626';
  else if (color.includes('--grid-muted')) t.textDark = '#94a3b8';
  else if (color.includes('--grid-subtext')) t.textDark = '#64748b';
  else if (color && !color.includes('var(')) t.textDark = color; // literal accent (e.g. currency colour)

  const fw = String(st.fontWeight || '');
  if ((fw === '600' || fw === '700') && !t.baseFontStyle) t.baseFontStyle = '600 12px';

  return Object.keys(t).length ? t : undefined;
};

const fmtNum = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// Translate one ReactGrid cell object into a Glide cell.
const toGlideCell = (cell) => {
  if (!cell) return gcell.text('', { readonly: true });
  const theme = themeFromStyle(cell);
  if (cell.type === 'number') {
    return gcell.number(cell.value, {
      readonly: !!cell.nonEditable,
      display: fmtNum(cell.value),
      theme,
    });
  }
  // 'text' and 'header' (totals labels / blanks) both render as text.
  const readonly = cell.type === 'header' ? true : !!cell.nonEditable;
  return gcell.text(cell.text ?? '', { readonly, theme });
};

const MantleGrid = ({
  rows,
  columns,
  isLoading = false,
  onCellsChanged,
  onFocusLocationChanging,
  // Optional per-column filter menus: mark a column `hasMenu: true` and this
  // fires with (columnId, bounds) when its header ▾ is clicked — the hook the
  // gridFilters FilterPopover attaches to.
  onHeaderMenuClick,
  toolbar = null,
  hint = null,
  stickyTopRows = 1, // rows[0] is always the header; extra sticky rows just render first
  stickyBottomRows = 1, // eslint-disable-line no-unused-vars -- no bottom pinning in Glide
  stickyLeftColumns = 1,
  enableRangeSelection = true, // eslint-disable-line no-unused-vars -- always on in Glide
  enableFillHandle = true, // eslint-disable-line no-unused-vars
  enableRowSelection = true, // eslint-disable-line no-unused-vars
  gridProps = {},
}) => {
  const headerRow = rows?.[0];
  const dataRows = useMemo(() => (rows || []).slice(1), [rows]);
  const dataRowsRef = useRef([]);
  dataRowsRef.current = dataRows;
  const columnsRef = useRef([]);
  columnsRef.current = columns || [];

  const gcols = useMemo(() => (columns || []).map((c, i) => ({
    id: String(c.columnId),
    title: headerRow?.cells?.[i]?.text ?? '',
    width: c.width || 100,
    ...(c.hasMenu ? { hasMenu: true } : {}),
  })), [columns, headerRow]);

  const rowCount = dataRows.length;

  const getCellContent = useCallback(([col, row]) => {
    const r = dataRows[row];
    return toGlideCell(r?.cells?.[col]);
  }, [dataRows]);

  const rowHeight = useCallback((r) => dataRowsRef.current[r]?.height || GRID.size.dataRow, []);

  const handleCellEdited = useCallback(([col, row], newVal) => {
    if (!onCellsChanged) return;
    const r = dataRowsRef.current[row];
    const orig = r?.cells?.[col];
    if (!r || !orig) return;
    const columnId = columnsRef.current[col]?.columnId;
    const newCell = orig.type === 'number'
      ? { ...orig, value: Number(newVal.data) || 0, text: String(Number(newVal.data) || 0) }
      : { ...orig, text: newVal.data != null ? String(newVal.data) : '', value: newVal.data };
    onCellsChanged([{ rowId: r.rowId, columnId, type: orig.type, previousCell: orig, newCell }]);
  }, [onCellsChanged]);

  // Cell clicks → the old focus-changing hook (how legacy pages do click-actions
  // like ✕ delete columns, toggles, and drill-downs).
  const handleCellClicked = useCallback(([col, row], event) => {
    const r = dataRowsRef.current[row];
    if (!r) return;
    // `bounds` rides along so a page can anchor a PickerPopover under the cell
    // that was clicked. Additive: every existing caller destructures rowId and
    // columnId and is unaffected by an extra key.
    const loc = {
      rowId: r.rowId,
      columnId: columnsRef.current[col]?.columnId,
      bounds: event?.bounds,
    };
    gridProps.onFocusLocationChanged?.(loc);
    onFocusLocationChanging?.(loc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onFocusLocationChanging, gridProps.onFocusLocationChanged]);

  const handleHeaderClicked = useCallback((colIndex) => {
    onFocusLocationChanging?.({ rowId: 'header', columnId: columnsRef.current[colIndex]?.columnId });
  }, [onFocusLocationChanging]);

  // Reported by columnId (not index) to match this adapter's columnId contract.
  const handleHeaderMenu = useCallback((colIndex, bounds) => {
    onHeaderMenuClick?.(columnsRef.current[colIndex]?.columnId, bounds);
  }, [onHeaderMenuClick]);

  // Synthesize ReactGrid-shaped selection ranges for pages that track them
  // (e.g. StockedPricing's "Change currency" acts on the selected cells).
  const handleSelectionChanged = useCallback((sel) => {
    if (!gridProps.onSelectionChanged) return;
    const range = sel?.current?.range;
    if (!range) { gridProps.onSelectionChanged([]); return; }
    const rowsInRange = dataRowsRef.current
      .slice(range.y, range.y + range.height)
      .map((r) => ({ rowId: r.rowId }));
    const colsInRange = columnsRef.current
      .slice(range.x, range.x + range.width)
      .map((c) => ({ columnId: c.columnId }));
    gridProps.onSelectionChanged([{ rows: rowsInRange, columns: colsInRange }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridProps.onSelectionChanged]);

  if (isLoading) {
    return (
      <div style={{ padding: '10px 14px', fontSize: 12, color: GRID.color.muted, background: GRID.color.readonlyBg }}>
        Loading…
      </div>
    );
  }

  if (!headerRow) {
    return (
      <div style={{ padding: '10px 14px', fontSize: 12, color: GRID.color.muted }}>
        No data.
      </div>
    );
  }

  return (
    <MantleDataGrid
      columns={gcols}
      rowCount={rowCount}
      getCellContent={getCellContent}
      onCellEdited={handleCellEdited}
      onCellClicked={handleCellClicked}
      onHeaderClicked={handleHeaderClicked}
      onHeaderMenuClick={onHeaderMenuClick ? handleHeaderMenu : undefined}
      onSelectionChanged={gridProps.onSelectionChanged ? handleSelectionChanged : undefined}
      freezeColumns={stickyLeftColumns}
      rowHeight={rowHeight}
      headerHeight={headerRow.height || GRID.size.headerRow}
      toolbar={toolbar}
      hint={hint}
    />
  );
};

// Small shared button so toolbars look consistent across grids.
// `accent` paints it green — used by Save buttons when there are unsaved
// changes, so the prompt to save is impossible to miss.
export const MantleGridButton = ({ children, onClick, disabled = false, accent = false }) => {
  const hot = accent && !disabled;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        fontSize: 11, padding: '3px 10px', borderRadius: 6, fontWeight: hot ? 600 : 400,
        border: `1px solid ${hot ? '#047857' : 'var(--grid-border-strong)'}`,
        background: hot ? '#059669' : 'var(--grid-readonly-bg)',
        color: hot ? '#ffffff' : 'var(--grid-subtext)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background-color 120ms, border-color 120ms, color 120ms',
      }}
    >
      {children}
    </button>
  );
};

export default React.memo(MantleGrid);
